import {IActiveDestination} from '../models/Destination';
import {INormalizedProduct} from '../models/NormalizedProduct';
import {workspaceItemsEndpoints} from '../store/endpoints/WorkspaceItemsEndpoints';
import {productBundleApi} from '../store/endpoints/ProductBundleEndpoints';

export interface IAddProductOptions {
    source?: string;
    priceLevel?: string;
    quantity?: number;
}

/**
 * Adds one product to a destination, dispatching the endpoint directly rather than through a
 * component-bound mutation hook. That lets the same code path serve both the per-row Add button
 * and the bulk thunk (which has no component to hang hooks off), so the two can't diverge.
 *
 * Resolves true on success and never throws — callers tally results instead of unwinding.
 *
 * Storefront destinations are deliberately not handled: a listing needs markup/featured settings,
 * so those go through BulkStorefrontPanel. Callers gate on `needsConfiguration`.
 */
export const addProductToDestination = async (
    dispatch: any,
    destination: IActiveDestination,
    product: INormalizedProduct,
    options?: IAddProductOptions,
): Promise<boolean> => {
    if (!product.id) return false;

    let handle: any;
    try {
        if (destination.type === 'workspace') {
            handle = dispatch(
                workspaceItemsEndpoints.endpoints.addProductToWorkspace.initiate({
                    workspace: destination.id,
                    product,
                    req: {
                        source: options?.source,
                        priceLevelPartNumber: options?.priceLevel,
                        quantity: options?.quantity,
                    },
                })
            );
        } else if (destination.type === 'bundle') {
            if (!product.catalog) return false;
            handle = dispatch(
                productBundleApi.endpoints.addItemToProductBundle.initiate({
                    bundleId: destination.id,
                    catalog: product.catalog,
                    productId: product.id,
                })
            );
        } else {
            return false;
        }

        const result = await handle.unwrap();
        return result?.success === true;
    } catch {
        return false;
    } finally {
        // Mutations dispatched via initiate() have no component subscription, so their cache
        // entries would otherwise pile up across a bulk run.
        handle?.reset?.();
    }
};

export default addProductToDestination;
